(ns practitest.frontend.search.events
  (:require
   [re-frame.core                     :as re-frame]
   [day8.re-frame.http-fx]
   [reagent-modals.modals             :refer [close-modal!]]
   [ajax.core                         :as ajax]
   [ajax.url                          :refer [params-to-str]]
   [clojure.string                    :as string]
   [practitest.frontend.common-old-fe.logging           :as log]))

;; ===========================================================================
;; constants

(def ^:const request-timeout 5000)
(def ^:const long-request-timeout 10000)
(def ^:const autosave-period 60000)
(def ^:const max-idle-minutes 60)

;; ===========================================================================
;; interceptors

(def common-interceptors
  [(when ^boolean js/goog.DEBUG re-frame/debug)
   re-frame/trim-v])

;; ===========================================================================
;; event handlers

(re-frame/reg-event-fx
 :search/initialize
 common-interceptors
 (fn [{:keys [db]} [{:keys [search-uri users-uri results per-page] :as options}]]
   (log/info :search/initialize options db)
   (.addEventListener js/window "popstate"
                      (fn [e]
                        (log/info "popstate" e)
                        (re-frame/dispatch [:search/set-query-state
                                            (js->clj (.-state e) :keywordize-keys true)])))
   (if-let [query-state (js->clj (.-state js/window.history) :keywordize-keys true)]
     (do
       (log/info :search/initialize "got state" query-state)
       {:db (merge db
                   {:search-uri   search-uri
                    :users-uri    users-uri
                    :per-page     per-page
                    :current-page 1
                    :query-state  query-state})
        :dispatch [:search/search]})
     {:db (merge results
                 db
                 {:search-uri   search-uri
                  :users-uri    users-uri
                  :per-page     per-page
                  :current-page 1
                  :query-state  {:page     1
                                 :query    (:query results)
                                 :filters  {}
                                 :per_page per-page}})})))

(re-frame/reg-event-db
  :search/init-complete
  (fn [db _]
    (assoc db :init-complete? true)))

(re-frame/reg-event-fx
 :search/set-query-state
 common-interceptors
 (fn [{:keys [db]} [value]]
   {:db       (merge db {:current-page 1
                         :query-state  value})
    :dispatch [:search/search]}))

(re-frame/reg-event-fx
 :search/set-table-size
 common-interceptors
 (fn [{:keys [db]} [value]]
   {:db       (-> db
                  (assoc :per-page value)
                  (assoc-in [:query-state :per_page] value)
                  (assoc-in [:query-state :page] 1))
    :dispatch [:search/search]}))

(re-frame/reg-event-fx
 :search/set-page
 common-interceptors
 (fn [{:keys [db]} [page]]
   {:db       (assoc-in db [:query-state :page] page)
    :dispatch [:search/search]}))

(re-frame/reg-event-db
  :search/users-received
  common-interceptors
  (fn [db [users]]
    (assoc db :search-users users)))

(re-frame/reg-event-db
  :search/et-bad-http-result
  common-interceptors
  (fn [db [error]]
    (log/debug ":search/et-bad-http-result" (:response error))
    (close-modal!)
    (-> db
        (dissoc :loading? :modal-loading?)
        (assoc :errors (get-in error [:response :errors])))))

(re-frame/reg-event-fx
 :search/get-users
 common-interceptors
 (fn [{:keys [db]} _]
   {:http-xhrio {:method          :get
                 :uri             (:users-uri db)
                 :timeout         request-timeout
                 :vec-strategy    :rails
                 :format          (ajax/url-request-format :rails)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [:search/users-received]
                 :on-failure      [:search/et-bad-http-result]}}))

(re-frame/reg-event-db
 :search/data-good
 common-interceptors
 (fn [db [facet num value]]
   (merge db (:results value) {:current-page (:page value) :filters {(:field_display_name facet) num}})))

(re-frame/reg-event-db
 :search/bad-http-result
 common-interceptors
 (fn [db [error]]
   (log/debug ":search/bad-http-result" (:response error))))

(re-frame/reg-event-fx
 :search/set-filter
 common-interceptors
 (fn [{:keys [db]} [facet value]]
   {:db (update db :query-state
                (fn [state]
                  (-> state
                      (assoc :page 1)
                      (update :filters assoc (:facet_id facet)
                              {:display_name (:field_display_name facet)
                               :is_user      (:is_user facet)
                               :value        value}))))
    :dispatch [:search/search]}))

(re-frame/reg-event-db
 :search/set-expand
 common-interceptors
 (fn [db [expand?]]
   (log/info "expand clicked" expand?)
   (update db :facets
           (fn [facets]
             (map #(assoc % :active expand?) facets)))))

(re-frame/reg-event-fx
 :search/clear-filter
 common-interceptors
 (fn [{:keys [db]} [facet-id]]
   {:db       (-> db
                  (update-in [:query-state :filters] dissoc facet-id)
                  (update-in [:query-state :facets] dissoc facet-id))
    :dispatch [:search/search]}))

(re-frame/reg-event-fx
 :search/search
 common-interceptors
 (fn [{:keys [db]} [new-query?]]
   {:db         (assoc db :searching? true)
    :http-xhrio {:method          :get
                 :uri             (:search-uri db)
                 :params          (assoc (if new-query?
                                           (dissoc (:query-state db) :page)
                                           (:query-state db))
                                         :screw-cache true)
                 :timeout         request-timeout
                 :vec-strategy    :rails
                 :format          (ajax/url-request-format :rails)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [:search/search-results]
                 :on-failure      [:search/data-bad]}}))

(re-frame/reg-event-db
 :search/search-results
 common-interceptors
 (fn [db [value]]
   (let [url (str js/window.location.pathname "?" (params-to-str :rails (:query-state db)))]
     (.pushState js/window.history
                 (clj->js (:query-state db))
                 "Search results"
                 url))
   (merge db (:results value) {:searching? false
                               :didyoumean (:didyoumean value)})))

(re-frame/reg-event-db
 :search/data-bad
 common-interceptors
 (fn [db [error]]
   (log/debug ":search/data-bad-result" (:response error))))

(re-frame/reg-event-db
 :search/set-chart-facet-active
 common-interceptors
 (fn [db [facet]]
   (log/info "facet chart clicked" facet)
   (update db :facets
           (fn [facets]
             (map #(if (= (:facet_id facet) (:facet_id %)) (update % :active not) %)
                  facets)))))

(re-frame/reg-event-fx
 :search/open-tips
 common-interceptors
 (fn []
   (log/info "tips box clicked!")))

(re-frame/reg-event-db
  :search/update-query
  (fn [db [_ new-query]]
    (assoc-in db [:query-state :query] new-query)))

(re-frame/reg-event-db
  :search/reset-page
  common-interceptors
  (fn [db [_]]
    (assoc-in db [:query-state :page] 1)))

(re-frame/reg-event-db
  :search/toggle-facet-value
  (fn [db [_ facet-id value]]
    (let [selected (get-in db [:query-state :facets facet-id] #{})]
      (if (contains? selected value)
        (re-frame/dispatch [:search/clear-filter facet-id])
        (assoc-in db [:query-state :facets facet-id] (conj selected value))))))

(re-frame/reg-sub
  :search/selected-facet-values
  (fn [db [_ facet-id]]
    (get-in db [:query-state :facets facet-id] #{})))
